/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Checkbox, Spinner, ConfirmationModal } from '@crc-evans/ui-components';
import MetricCardWithComment from './MetricCardWithComment';
import { Info, Save, Plus, Trash2 } from 'lucide-react';
import { message } from 'antd';
import axiosConfig from '../../services/axiosConfig';
import { useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';
import CustomRootSelect from './CustomRootSelect';
import { isOffshoreProject } from '../../utils/projectConfig';

/** Convert display name to camelCase keyName (e.g. "safety error test" -> "safetyErrorTest") */
const toCamelCase = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .split(/\s+|[_-]+/)
    .filter(Boolean)
    .map((word, i) => {
      const w = word.replace(/[^a-zA-Z0-9]/g, '');
      if (!w) return '';
      return i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join('');
};

/** Offshore DPR quality metrics — API keyNames from GET `/dpr-quality`. */
const OFFSHORE_QUALITY_API_KEYS = new Set([
  'cutOutReweldStn1',
  'ndtRepair',
  'ndtCutout',
  'reweldStn1DueToNdtCutOut',
]);

const OFFSHORE_QUALITY_FIELD_DEFS = [
  {
    apiKeyName: 'cutOutReweldStn1',
    id: 'offshore-st1-cutout-reweld',
    title: 'Cut-out + Reweld in Station 1',
    commentPlaceholder: 'e.g. blow-through, porosity, or client visual inspection camera findings',
    order: 1,
  },
  {
    apiKeyName: 'ndtRepair',
    id: 'offshore-ndt-repair',
    title: 'NDT Repair in NDT Station',
    commentPlaceholder: 'Root cause and repair details reported separately',
    order: 2,
  },
  {
    apiKeyName: 'ndtCutout',
    id: 'offshore-ndt-cutout',
    title: 'NDT Cut-out in NDT Station',
    commentPlaceholder: 'Root cause and repair details reported separately',
    order: 3,
  },
  {
    apiKeyName: 'reweldStn1DueToNdtCutOut',
    id: 'offshore-st1-rewelds-from-ndt-cutout',
    title: 'Reweld in Station 1 (Due to NDT Cut-Out)',
    commentPlaceholder: 'e.g. NDT at Station 4 → 4 rewelds in ST1 per 1 NDT cut-out',
    order: 4,
  },
];

function isOffshoreQualityMetric(metric) {
  if (!metric) return false;
  const key = String(metric.keyName || '').trim();
  if (key && OFFSHORE_QUALITY_API_KEYS.has(key)) return true;
  return String(metric.category || '').trim() === 'offshoreDefects';
}

function buildDefaultOffshoreQualityCards() {
  return OFFSHORE_QUALITY_FIELD_DEFS.map((def) => ({
    id: def.id,
    apiKeyName: def.apiKeyName,
    title: def.title,
    commentPlaceholder: def.commentPlaceholder,
    order: def.order,
    month: 0,
    total: 0,
    summary: 0,
    today: 0,
    baseMonth: 0,
    baseTotal: 0,
    originalMetric: {
      keyName: def.apiKeyName,
      name: def.title,
      category: 'offshoreDefects',
      categoryLabel: 'Defect Tracking',
      type: 'number',
      editable: true,
      order: def.order,
    },
  }));
}

function mapOffshoreQualityCardsFromApi({
  qualityMetricsData,
  offshoreQualitySummary,
  legacyOffshoreQuality,
}) {
  const offshoreRows = (Array.isArray(qualityMetricsData) ? qualityMetricsData : []).filter(
    isOffshoreQualityMetric
  );
  const legacy = legacyOffshoreQuality && typeof legacyOffshoreQuality === 'object'
    ? legacyOffshoreQuality
    : {};

  return OFFSHORE_QUALITY_FIELD_DEFS.map((def) => {
    const metric = offshoreRows.find((m) => String(m.keyName || '').trim() === def.apiKeyName);
    const summaryBlock = offshoreQualitySummary?.[def.apiKeyName];
    const legacyToday = (() => {
      if (def.apiKeyName === 'cutOutReweldStn1') {
        // Do not fall back to reweldJointsSt1 / st1Cutout — those are different metrics
        if (legacy.cutOutReweldStn1 != null) return legacy.cutOutReweldStn1;
        if (legacy.st1CutoutReweld != null) return legacy.st1CutoutReweld;
        return null;
      }
      if (def.apiKeyName === 'reweldStn1DueToNdtCutOut') {
        if (legacy.reweldStn1DueToNdtCutOut != null) return legacy.reweldStn1DueToNdtCutOut;
        if (legacy.st1ReweldsFromNdtCutout != null) return legacy.st1ReweldsFromNdtCutout;
        return null;
      }
      if (def.apiKeyName === 'ndtRepair') {
        return legacy.ndtRepair != null ? legacy.ndtRepair : null;
      }
      if (def.apiKeyName === 'ndtCutout') {
        return legacy.ndtCutout != null ? legacy.ndtCutout : null;
      }
      return null;
    })();

    const pickNum = (...candidates) => {
      for (let i = 0; i < candidates.length; i += 1) {
        const v = candidates[i];
        if (v == null || v === '') continue;
        const n = Number(v);
        if (Number.isFinite(n)) return n;
      }
      return 0;
    };

    // Prefer metric row for this keyName; then summary block; then legacy aliases
    const todayValue = pickNum(metric?.todayValue, summaryBlock?.today, legacyToday);
    const monthValue = pickNum(metric?.toMonth, summaryBlock?.toMonth);
    const totalValue = pickNum(metric?.toDateValue, summaryBlock?.toDate);

    return {
      id: def.id,
      apiKeyName: def.apiKeyName,
      title: metric?.name || def.title,
      commentPlaceholder: def.commentPlaceholder,
      order: def.order,
      month: monthValue,
      total: totalValue,
      summary: todayValue,
      today: todayValue,
      baseMonth: Math.max(0, monthValue - todayValue),
      baseTotal: Math.max(0, totalValue - todayValue),
      originalMetric: metric || {
        keyName: def.apiKeyName,
        name: def.title,
        category: 'offshoreDefects',
        categoryLabel: 'Defect Tracking',
        type: 'number',
        editable: true,
        order: def.order,
      },
    };
  });
}

/** Pipe-tally / legacy metric dropped from UI and merge. */
const EXCLUDED_QUALITY_KEY_NAMES = new Set(['Cut_Out_S1_Shift_Total']);

/** Map API keyName → display title (covers old API names). */
const AUT_METRIC_DISPLAY_TITLE = {
  S1_Cut_Out_DueTo_AUT_Reject: 'Cut Out Due To AUT Reject',
  S1_Reweld_DueTo_AUT_Reject: 'Reweld Due To AUT Reject',
  AUT_Cut_Out_Shift_Total: 'AUT Cut Out',
  AUT_Repair_Shift_Total: 'AUT Repair',
};

/** Used when normalizing station rejects from GET `/dpr-quality` (UI removed; payload preserved). */
const ROOT_OPTIONS = ['Root', 'Hot', 'Fills', 'Cap', 'Others'];

/** UI shows exactly two defect cards; values persist under stable API keyNames. */
const CANONICAL_REPAIR_CARD = {
  id: 'reject-due-to-repair',
  title: 'Reject Due to Repair',
  month: 0,
  total: 0,
  summary: 0,
  today: 0,
  hasToday: true,
  baseMonth: 0,
  baseTotal: 0,
  originalMetric: {
    keyName: 'RejectDueToRepair',
    name: 'Reject Due to Repair',
    order: 1
  }
};

const CANONICAL_CUTOUT_CARD = {
  id: 'rejected-due-to-cutout',
  title: 'Rejected due to Cutout',
  month: 0,
  total: 0,
  summary: 0,
  today: 0,
  hasToday: true,
  baseMonth: 0,
  baseTotal: 0,
  originalMetric: {
    keyName: 'RejectedDueToCutout',
    name: 'Rejected due to Cutout',
    order: 2
  }
};

function cloneTwoCardDefaults() {
  return [
    {
      ...CANONICAL_REPAIR_CARD,
      originalMetric: { ...CANONICAL_REPAIR_CARD.originalMetric }
    },
    {
      ...CANONICAL_CUTOUT_CARD,
      originalMetric: { ...CANONICAL_CUTOUT_CARD.originalMetric }
    }
  ];
}

/** Lowercase keyName for routing metrics into UI buckets. */
function normalizedQualityKeyName(item) {
  return String(item.originalMetric?.keyName || '')
    .trim()
    .toLowerCase();
}

/**
 * Metrics merged into the "Reject Due to Repair" card only.
 * Excludes category "repairs" rows like rtRepair — those stay orphans so comments do not duplicate.
 */
const WELD_OUTCOME_REPAIR_CARD_KEYS = new Set([
  'rejectduetorepair',
  'rejectedduetorepair',
  's1_reweld_dueto_aut_reject',
  'aut_repair_shift_total'
]);

/**
 * Metrics merged into the "Rejected due to Cutout" card only.
 * Excludes rtCutOff / unrelated "cutout" substring matches.
 */
const WELD_OUTCOME_CUTOUT_CARD_KEYS = new Set([
  'rejectedduetocutout',
  'rejectduetocutout',
  's1_cut_out_dueto_aut_reject',
  'aut_cut_out_shift_total'
]);

function isWeldOutcomeCutoutCardMetric(item) {
  const k = normalizedQualityKeyName(item);
  return Boolean(k) && WELD_OUTCOME_CUTOUT_CARD_KEYS.has(k);
}

function isWeldOutcomeRepairCardMetric(item) {
  const k = normalizedQualityKeyName(item);
  if (!k || WELD_OUTCOME_CUTOUT_CARD_KEYS.has(k)) return false;
  return WELD_OUTCOME_REPAIR_CARD_KEYS.has(k);
}

function partitionQualityMetricsThreeWay(items) {
  const repair = [];
  const cutout = [];
  const orphan = [];
  for (const it of items || []) {
    if (isWeldOutcomeCutoutCardMetric(it)) cutout.push(it);
    else if (isWeldOutcomeRepairCardMetric(it)) repair.push(it);
    else orphan.push(it);
  }
  return { repair, cutout, orphan };
}

function mergeCommentsForBucket(bucket, commentsByItemId) {
  const parts = [];
  for (const it of bucket) {
    const c = String(commentsByItemId[it.id] ?? '').trim();
    if (!c) continue;
    parts.push(c);
  }
  return parts.join('\n\n');
}

function mergeBucketIntoCanonicalCard(bucketItems, template) {
  if (!bucketItems.length) {
    return {
      ...template,
      originalMetric: { ...template.originalMetric }
    };
  }
  let summary = 0;
  let month = 0;
  let total = 0;
  for (const it of bucketItems) {
    summary += Number(it.summary ?? 0);
    month += Number(it.month ?? 0);
    total += Number(it.total ?? 0);
  }
  const baseMonth = Math.max(0, month - summary);
  const baseTotal = Math.max(0, total - summary);
  const primaryOm = bucketItems[0].originalMetric || {};
  return {
    ...template,
    summary,
    today: summary,
    month,
    total,
    baseMonth,
    baseTotal,
    originalMetric: {
      ...template.originalMetric,
      ...(primaryOm.id != null ? { id: primaryOm.id } : {})
    }
  };
}

const QualityLog = ({
  qualityData: initialQualityData = [],
  onSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId,
  selectedProject,
  isShiftSkipped = false,
  isLastDprSection
}) => {
  const isOffshore = isOffshoreProject(selectedProject);
  const [qualityData, setQualityData] = useState(initialQualityData);
  /** API metrics not mapped into the two canonical cards (passed through on save). */
  const [orphanQualityMetrics, setOrphanQualityMetrics] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [stationRejects, setStationRejects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reportNo, setReportNo] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [newlyAddedId, setNewlyAddedId] = useState(null);
  const dispatch = useDispatch();
  const skipQuality = useSelector(state => {
    if (!sowId) return false;
    return state.dprReducer?.skipStatesBySection?.[sowId]?.skipQuality || false;
  });
  const userAccessList = useSelector((state) => state.equip?.userAccessList || []);
  const projectTeamMembersAccessList = useSelector(
    (state) => state.equip?.projectTeamMembersAccessList || []
  );

  const [isSkipConfirmOpen, setIsSkipConfirmOpen] = useState(false);

  const [cardComments, setCardComments] = useState({});
  const [stationErrors, setStationErrors] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [stationToDelete, setStationToDelete] = useState(null);

  const [offshoreQualityCards, setOffshoreQualityCards] = useState(() => buildDefaultOffshoreQualityCards());
  const [offshoreQualityComments, setOffshoreQualityComments] = useState({});

  useEffect(() => {
    if (!isOffshore) {
      setOffshoreQualityCards(buildDefaultOffshoreQualityCards());
      setOffshoreQualityComments({});
    }
  }, [isOffshore]);

  const updateOffshoreQualityComment = (apiKeyName, value) => {
    setOffshoreQualityComments((prev) => ({ ...prev, [apiKeyName]: value }));
  };

  const buildOffshoreCommentNotes = () => {
    const lines = [];
    offshoreQualityCards.forEach(({ apiKeyName, title }) => {
      const text = String(offshoreQualityComments[apiKeyName] || '').trim();
      if (!text) return;
      lines.push(`${title}: ${text.replace(/\n/g, ' ')}`);
    });
    return lines.join('\n');
  };

  const defaultQualityData = useMemo(() => cloneTwoCardDefaults(), []);

  const isWriteAccess = useMemo(() => {
    const groups =
      userAccessList?.length > 0
        ? userAccessList
        : (() => {
          try {
            return JSON.parse(localStorage.getItem('UserGroup') || '[]');
          } catch (e) {
            return [];
          }
        })();
    if (Array.isArray(groups) && (groups.includes('admin') || groups.includes('super-admin')))
      return true;
    if (projectTeamMembersAccessList?.[0]?.isGuestUser) return false;
    if (
      projectTeamMembersAccessList?.[0]?.isNormalUser &&
      !projectTeamMembersAccessList?.[0]?.tabPermissions?.Details?.write
    )
      return false;
    return true;
  }, [projectTeamMembersAccessList, userAccessList]);

  const updateCardComment = (id, val) =>
    setCardComments((prev) => ({ ...prev, [id]: val }));

  const buildCardCommentNotes = (data) => {
    const lines = [];
    (data || []).forEach((item) => {
      const text = String(cardComments[item.id] || '').trim();
      if (!text) return;
      lines.push(text.replace(/\n/g, ' '));
    });
    return lines.join('\n');
  };

  const resolvedQualityData = useMemo(() => {
    return qualityData.length >= 2 ? qualityData : defaultQualityData;
  }, [qualityData, defaultQualityData]);

  const handleSkipQualityChange = (checked) => {
    if (!sowId) return;
    if (checked) {
      setIsSkipConfirmOpen(true);
    } else {
      dispatch(updateDprSkipState(sowId, 'skipQuality', false));
    }
  };

  const handleConfirmSkipQuality = () => {
    if (sowId) dispatch(updateDprSkipState(sowId, 'skipQuality', true));
    setIsSkipConfirmOpen(false);
  };

  // Effect to scroll to newly added item
  useEffect(() => {
    if (newlyAddedId) {
      // Small timeout to allow render
      const timer = setTimeout(() => {
        const element = document.getElementById(newlyAddedId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setNewlyAddedId(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [newlyAddedId, qualityData]);

  // Fetch quality data from API when shift changes
  const fetchQualityData = useCallback(async () => {
    if (!dprLogId) return;
    setIsLoading(true);
    try {
      const response = await axiosConfig.get(`/dpr-quality?dprLogId=${dprLogId}&shift=${shift}`);

      let qualityMetricsData = null;
      let stationRejectsData = null;
      let notesData = null;
      let sectionCommentsData = null;
      let reportNoData = null;
      let offshoreQualitySummary = null;
      let legacyOffshoreQuality = null;

      if (response.data && response.data.data) {
        qualityMetricsData = response.data.data.qualityMetrics;
        stationRejectsData = response.data.data.stationRejects;
        notesData = response.data.data.notes;
        sectionCommentsData = response.data.data.comments;
        reportNoData = response.data.data.reportNo;
        offshoreQualitySummary = response.data.data.offshoreQualitySummary;
        legacyOffshoreQuality = response.data.data.offshoreQuality;
      }

      if (reportNoData) {
        setReportNo(reportNoData);
      }

      if (isOffshore) {
        const cards = mapOffshoreQualityCardsFromApi({
          qualityMetricsData,
          offshoreQualitySummary,
          legacyOffshoreQuality,
        });
        setOffshoreQualityCards(cards);
        const commentsByKey = {};
        cards.forEach((card) => {
          const c = card.originalMetric?.comments;
          commentsByKey[card.apiKeyName] = c != null ? String(c) : '';
        });
        setOffshoreQualityComments(commentsByKey);
        setQualityData([]);
        setOrphanQualityMetrics([]);
        setCardComments({});
      } else if (Array.isArray(qualityMetricsData)) {
        const commentsByItemId = {};
        const sorted = [...qualityMetricsData].sort(
          (a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0)
        );
        const mappedQualityData = [];
        sorted.forEach((metric, index) => {
          const keyName = metric.keyName != null ? String(metric.keyName) : '';
          if (keyName && EXCLUDED_QUALITY_KEY_NAMES.has(keyName)) {
            return;
          }
          if (isOffshoreQualityMetric(metric)) {
            return;
          }
          const currentMonth = metric.toMonth || 0;
          const currentTotal = metric.toDateValue || 0;
          const currentSummary = metric.todayValue || 0;
          const titleOverride = keyName && AUT_METRIC_DISPLAY_TITLE[keyName];
          const keySlug =
            keyName && String(keyName).trim()
              ? String(keyName).replace(/[^a-zA-Z0-9_-]/g, '') || `kn-${index}`
              : metric.id != null && String(metric.id).trim() !== ''
                ? String(metric.id)
                : 'noid';
          // Include index so duplicate API `id` values (e.g. two rows with id "3") never collide in comment maps.
          const id = `quality-metric-${index}-${keySlug}`;
          mappedQualityData.push({
            id,
            title: titleOverride || metric.name || '',
            month: currentMonth,
            total: currentTotal,
            summary: currentSummary,
            today: currentSummary,
            hasToday: true,
            baseMonth: Math.max(0, currentMonth - currentSummary),
            baseTotal: Math.max(0, currentTotal - currentSummary),
            originalMetric: metric
          });
          const c = metric.comments;
          commentsByItemId[id] = c != null ? String(c) : '';
        });
        const { repair, cutout, orphan } = partitionQualityMetricsThreeWay(mappedQualityData);
        const repairCard = mergeBucketIntoCanonicalCard(repair, CANONICAL_REPAIR_CARD);
        const cutoutCard = mergeBucketIntoCanonicalCard(cutout, CANONICAL_CUTOUT_CARD);
        setQualityData([repairCard, cutoutCard]);
        setOrphanQualityMetrics(orphan.map((o) => o.originalMetric).filter(Boolean));
        setCardComments({
          [repairCard.id]: mergeCommentsForBucket(repair, commentsByItemId),
          [cutoutCard.id]: mergeCommentsForBucket(cutout, commentsByItemId)
        });
      } else {
        setQualityData([]);
        setOrphanQualityMetrics([]);
        setCardComments({});
      }

      // Map stationRejects
      if (stationRejectsData && Array.isArray(stationRejectsData) && stationRejectsData.length > 0) {
        const mappedStationRejects = stationRejectsData.map((reject, index) => {
          const incomingStationNumber = reject.stationNumber || reject.station || '';
          const incomingStationName = reject.stationName || reject.root || '';

          let rootValue = 'Root';
          let stationNameValue = '';

          // Check if the incoming name is one of the standard options (excluding 'Others' which is a fallback category in UI but here acts as limit)
          // Actually, if backend sends 'Others', it might mean literally 'Others' option selected without text? 
          // But usually 'Others' implies text input. 
          // If incomingStationName is a standard option, set it as root.
          if (ROOT_OPTIONS.includes(incomingStationName) && incomingStationName !== 'Others') {
            rootValue = incomingStationName;
          } else if (incomingStationName) {
            // Treat as custom value directly
            rootValue = incomingStationName;
            stationNameValue = incomingStationName; // Keep consistency
          }

          return {
            id: reject.id || `station-${index}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
            station: incomingStationNumber,
            stationName: stationNameValue,
            todayValue: reject.todayValue || 0,
            remarks: reject.remarks || '',
            root: rootValue,
            originalReject: reject
          };
        });
        setStationRejects(mappedStationRejects);
      } else {
        setStationRejects([]);
      }

      if (notesData) {
        setRemarks(notesData);
      } else if (sectionCommentsData) {
        setRemarks(sectionCommentsData);
      } else {
        setRemarks('');
      }
    } catch (error) {
      console.error('Error fetching quality data:', error);
      setQualityData([]);
      setOrphanQualityMetrics([]);
      setStationRejects([]);
      setRemarks('');
      setCardComments({});
      if (isOffshore) {
        setOffshoreQualityCards(buildDefaultOffshoreQualityCards());
        setOffshoreQualityComments({});
      }
    } finally {
      setIsLoading(false);
    }
  }, [shift, dprLogId, isOffshore]);

  useEffect(() => {
    fetchQualityData();
  }, [fetchQualityData]);

  const handleSummaryChange = (itemId, value) => {
    if (!itemId) return;
    const numValue = value === '' ? 0 : parseInt(value, 10) || 0;
    setQualityData(prevData => {
      const updatedData = prevData.map(item => {
        if (String(item.id) === String(itemId)) {
          return {
            ...item,
            summary: numValue,
            month: (item.baseMonth || 0) + numValue,
            total: (item.baseTotal || 0) + numValue
          };
        }
        return { ...item };
      });
      return updatedData;
    });
  };

  const handleOffshoreSummaryChange = (apiKeyName, value) => {
    if (!apiKeyName) return;
    const numValue = value === '' ? 0 : parseInt(value, 10) || 0;
    setOffshoreQualityCards((prev) =>
      prev.map((item) => {
        if (item.apiKeyName !== apiKeyName) return item;
        return {
          ...item,
          summary: numValue,
          today: numValue,
          month: (item.baseMonth || 0) + numValue,
          total: (item.baseTotal || 0) + numValue,
        };
      })
    );
  };

  const buildOffshoreQualityMetricsPayload = (cards = offshoreQualityCards) =>
    (cards || []).map((item) => {
      const originalMetric = item.originalMetric || {};
      const commentText = String(offshoreQualityComments[item.apiKeyName] ?? '').trim();
      return {
        ...originalMetric,
        keyName: item.apiKeyName,
        name: item.title || originalMetric.name || '',
        category: originalMetric.category || 'offshoreDefects',
        categoryLabel: originalMetric.categoryLabel || 'Defect Tracking',
        type: originalMetric.type || 'number',
        editable: originalMetric.editable !== false,
        order: item.order ?? originalMetric.order,
        todayValue: item.summary ?? 0,
        toDateValue: item.total ?? originalMetric.toDateValue ?? 0,
        toMonth: item.month ?? originalMetric.toMonth ?? 0,
        comments: commentText || originalMetric.comments || '',
      };
    });

  const buildQualityMetricsPayload = (currentQualityData = qualityData) => {
    const slotPayload = (currentQualityData || []).map((item) => {
      const originalMetric = item.originalMetric || {};
      const keyFromTitle = toCamelCase(item.title || '');
      const stableKey =
        originalMetric.keyName && String(originalMetric.keyName).trim()
          ? String(originalMetric.keyName).trim()
          : null;
      const keyName = stableKey || keyFromTitle || item.id;
      const commentText = String(cardComments[item.id] ?? '').trim();
      return {
        ...originalMetric,
        keyName: typeof keyName === 'string' ? keyName : String(keyName),
        name: item.title || originalMetric.name || '',
        todayValue: item.summary ?? 0,
        toDateValue: item.total ?? originalMetric.toDateValue ?? 0,
        toMonth: item.month ?? originalMetric.toMonth ?? 0,
        comments: commentText || originalMetric.comments || ''
      };
    });

    // Only send these 2 metrics (as requested)
    const allowed = new Set(['rejectduetorepair', 'rejectedduetocutout']);
    return slotPayload.filter((m) => allowed.has(String(m.keyName || '').toLowerCase()));
  };

  const buildStationRejectsPayload = (currentRejects = stationRejects) => {
    const rejects = currentRejects.map((reject) => {
      const originalReject = reject.originalReject || {};
      const payload = {
        stationNumber: reject.station || originalReject.station || '',
        todayValue: reject.todayValue || 0,
        remarks: reject.remarks || originalReject.remarks || ''
      };

      if (reject.id && reject.originalReject && reject.originalReject.id) {
        payload.id = reject.id;
      }

      // Logic for stationName: Prefer custom stationName (from "Others"), else use root
      const customStationName = reject.stationName || originalReject.stationName;
      const rootValue = reject.root || originalReject.root;

      if (customStationName) {
        payload.stationName = customStationName;
      } else if (rootValue) {
        payload.stationName = rootValue;
      }

      return payload;
    });
    return rejects;
  };

  // ---- Station Rejects: add/edit/delete + keep summary in sync ----
  const updateStationRejectSummary = (rejects) => {
    const total = (Array.isArray(rejects) ? rejects : []).reduce((sum, reject) => {
      const value = parseInt(reject?.todayValue, 10);
      return sum + (Number.isNaN(value) ? 0 : value);
    }, 0);

    setQualityData((prevData) =>
      (Array.isArray(prevData) ? prevData : []).map((item) => {
        const title = String(item.title || '').toLowerCase();
        const keyName = String(item.originalMetric?.keyName || '').toLowerCase();
        if (title.includes('station reject') || keyName.includes('station')) {
          return {
            ...item,
            summary: total,
            today: total,
            month: (item.baseMonth || 0) + total,
            total: (item.baseTotal || 0) + total,
          };
        }
        return item;
      })
    );
  };

  const handleAddStation = () => {
    const newStation = {
      id: `station-new-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      station: '',
      stationName: '',
      todayValue: 0,
      remarks: '',
      root: 'Root',
      originalReject: null,
    };
    const updated = [...(Array.isArray(stationRejects) ? stationRejects : []), newStation];
    setStationRejects(updated);
    updateStationRejectSummary(updated);
  };

  const handleDeleteStation = (id) => {
    setStationToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setStationToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!stationToDelete) return;
    const updated = (Array.isArray(stationRejects) ? stationRejects : []).filter(
      (s) => String(s.id) !== String(stationToDelete)
    );
    setStationRejects(updated);
    updateStationRejectSummary(updated);
    setIsDeleteModalOpen(false);
    setStationToDelete(null);
  };

  const handleStationChange = (id, field, value) => {
    if (!id) return;

    // Duplicate validation for station + root (+ stationName for Others)
    if (field === 'station' || field === 'root' || field === 'stationName') {
      const current = (Array.isArray(stationRejects) ? stationRejects : []).find(
        (s) => String(s.id) === String(id)
      );
      if (current) {
        const nextStation = field === 'station' ? value : current.station;
        const nextRoot = field === 'root' ? value : current.root;
        const nextStationName = field === 'stationName' ? value : current.stationName;
        if (nextStation && nextRoot) {
          const isDuplicate = (Array.isArray(stationRejects) ? stationRejects : []).some((s) => {
            if (String(s.id) === String(id)) return false;
            const sameStation = String(s.station) === String(nextStation);
            const sameRoot = String(s.root) === String(nextRoot);
            if (String(nextRoot) === 'Others') {
              return (
                sameStation &&
                sameRoot &&
                String(s.stationName || '') === String(nextStationName || '')
              );
            }
            return sameStation && sameRoot;
          });
          if (isDuplicate) {
            const errorMsg = `Station ${nextStation} with ${nextRoot}${
              String(nextRoot) === 'Others' ? ` (${nextStationName || ''})` : ''
            } already exists.`;
            message.warning(errorMsg);
            setStationErrors((prev) => ({ ...(prev || {}), [id]: errorMsg }));
            return;
          }
        }
      }
    }

    setStationErrors((prev) => {
      const next = { ...(prev || {}) };
      delete next[id];
      return next;
    });

    setStationRejects((prevRejects) => {
      const updated = (Array.isArray(prevRejects) ? prevRejects : []).map((s) => {
        if (String(s.id) !== String(id)) return s;
        if (field === 'root' && value !== 'Others') return { ...s, root: value, stationName: '' };
        return { ...s, [field]: value };
      });
      updateStationRejectSummary(updated);
      return updated;
    });
  };


  const handleSave = async () => {
    if (isShiftSkipped) return false;
    setIsSaving(true);
    try {
      if (!dprLogId) {
        message.warning('Cannot save: DPR log not loaded.');
        return false;
      }

      const qualityMetrics = isOffshore
        ? buildOffshoreQualityMetricsPayload()
        : buildQualityMetricsPayload();
      const rejects = buildStationRejectsPayload();

      const parseOQ = (v) => {
        const n = parseInt(String(v ?? '').replace(/\D/g, ''), 10);
        return Number.isFinite(n) ? n : undefined;
      };
      const offshoreCardValue = (apiKeyName) => {
        const card = offshoreQualityCards.find((c) => c.apiKeyName === apiKeyName);
        return parseOQ(card?.summary);
      };
      const cutOutReweldVal = offshoreCardValue('cutOutReweldStn1');
      const payload = {
        shift: shift,
        dprQuality: {
          dprLogId: dprLogId,
          reportNo: reportNo || `DPR-${dprLogId}-Q`,
          qualityMetrics: qualityMetrics,
          stationRejects: rejects,
          notes: [
            remarks || '',
            isOffshore ? '' : buildCardCommentNotes(resolvedQualityData),
            isOffshore ? buildOffshoreCommentNotes() : '',
          ].filter(Boolean).join('\n\n'),
          ...(isOffshore ? {
            offshoreQuality: {
              cutOutReweldStn1: cutOutReweldVal,
              st1CutoutReweld: cutOutReweldVal,
              // ST1 reweld used by productivity shift calc (Phase 3 mirror)
              reweldJointsSt1: cutOutReweldVal,
              ndtRepair: offshoreCardValue('ndtRepair'),
              ndtCutout: offshoreCardValue('ndtCutout'),
              // NDT-cutout-driven ST1 rewelds — must not overwrite reweldJointsSt1
              reweldStn1DueToNdtCutOut: offshoreCardValue('reweldStn1DueToNdtCutOut'),
              st1ReweldsFromNdtCutout: offshoreCardValue('reweldStn1DueToNdtCutOut'),
            },
          } : {}),
        }
      };

      await axiosConfig.put('/dpr-quality', payload);
      await fetchQualityData();

      if (onSave) {
        onSave({
          qualityData,
          stationRejects,
          remarks
        });
      }
      message.success('Quality report saved successfully.');
      return true;
    } catch (error) {
      console.error('Error saving quality report:', error);
      message.error(error?.response?.data?.message || error?.message || 'Failed to save quality report.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 1000
        }}>
          <Spinner variant="inline" size="medium" />
        </div>
      )}
      <div>
        {/* White container for all content */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '24px'
        }}>
          {/* Simple header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start'
            }}>
              <div style={{
                color: '#6366F1',
                backgroundColor: '#EEF2FF',
                padding: '8px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Info size={24} />
              </div>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 700, color: '#0F172A', textAlign: 'left' }}>Quality Control Log</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#64748B', textAlign: 'left' }}>Record daily weld status and defects.</p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Checkbox
                label="Skip Quality"
                checked={skipQuality}
                onChange={handleSkipQualityChange}
                borderColor="#00C9A7"
              />
            </div>
          </div>

          {/* Two Column Layout: Defect Tracking / Offshore Quality + Station Rejects */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            alignItems: 'start',
            marginBottom: '32px'
          }}>
            {/* Left Column: Defect Tracking (onshore) or Offshore Quality Inputs */}
            <div style={{
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: '#FFFFFF'
            }}>
              {isOffshore ? (
                <>
                  <h4 style={{
                    margin: '0 0 16px 0',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#1F2937',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: '#FEF3C7',
                      border: '2px solid #F59E0B',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: '#F59E0B',
                      fontWeight: 'bold'
                    }}>!</span>
                    Defect Tracking
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {offshoreQualityCards.map((item) => (
                      <MetricCardWithComment
                        key={item.id}
                        id={item.id}
                        title={item.title}
                        pills={[
                          { label: 'MONTH', value: item.month || 0 },
                          { label: 'TOTAL', value: item.total || 0 },
                        ]}
                        inputValue={item.summary || 0}
                        onInputChange={(val) => handleOffshoreSummaryChange(item.apiKeyName, val)}
                        disabled={item.originalMetric?.editable === false}
                        highlightBorderColor="#00C9A7"
                        comment={offshoreQualityComments[item.apiKeyName] || ''}
                        onCommentChange={(val) => updateOffshoreQualityComment(item.apiKeyName, val)}
                        commentPlaceholder={item.commentPlaceholder}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <h4 style={{
                    margin: '0 0 16px 0',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#1F2937',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: '#FEF3C7',
                      border: '2px solid #F59E0B',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: '#F59E0B',
                      fontWeight: 'bold'
                    }}>!</span>
                    Defect Tracking
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {resolvedQualityData.map((item) => {
                      const title = String(item.title || '').toLowerCase();
                      const keyName = String(item.originalMetric?.keyName || '').toLowerCase();
                      const isStationReject = title.includes('station reject') || keyName.includes('station');
                      const metricLocked = item.originalMetric && item.originalMetric.editable === false;

                      return (
                        <MetricCardWithComment
                          key={item.id}
                          id={item.id}
                          title={item.title}
                          pills={[
                            { label: 'MONTH', value: item.month || 0 },
                            { label: 'TOTAL', value: item.total || 0 },
                          ]}
                          inputValue={item.summary || 0}
                          onInputChange={(val) => handleSummaryChange(item.id, val)}
                          disabled={metricLocked}
                          inputDisabledOnly={isStationReject && !metricLocked}
                          highlightBorderColor="#00C9A7"
                          comment={cardComments[item.id] || ''}
                          onCommentChange={(val) => updateCardComment(item.id, val)}
                        />
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Right Column: Station Rejects Section */}
            <div style={{
              border: '1px solid #FEE2E2',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: '#FFF5F5'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#DC2626', textAlign: 'left' }}>
                  Station Rejects
                </h4>
                <Button
                  label="Add Station"
                  variant="tertiary"
                  size="small"
                  icon={Plus}
                  onClick={handleAddStation}
                  textColor="#DC2626"
                />
              </div>

              {stationRejects.length === 0 ? (
                <div style={{
                  color: '#94A3B8',
                  fontSize: '14px',
                  textAlign: 'center',
                  padding: '40px 20px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px'
                }}>
                  No station rejects recorded.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {stationRejects.map((station, index) => (
                    <div
                      key={station.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '6px',
                        border: '1px solid #FEE2E2',
                        overflow: 'visible'
                      }}
                    >
                      {/* Main Row */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px'
                        }}
                      >
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: '#FEE2E2',
                          color: '#DC2626',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 600,
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </div>

                        <select
                          value={station.station || ''}
                          onChange={(e) => handleStationChange(station.id, 'station', e.target.value)}
                          style={{
                            width: '160px',
                            padding: '8px 10px',
                            fontSize: '13px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            outline: 'none'
                          }}
                        >
                          <option value="">Station No.</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                            <option key={num} value={String(num)}>{num}</option>
                          ))}
                        </select>

                        <CustomRootSelect
                          value={station.root || ''}
                          onChange={(val) => handleStationChange(station.id, 'root', val)}
                          placeholder="Root"
                        />

                        <input
                          type="number"
                          min={0}
                          placeholder="Qty"
                          value={station.todayValue}
                          onChange={(e) => handleStationChange(station.id, 'todayValue', e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0)}
                          style={{
                            width: '100px',
                            padding: '7px 10px',
                            fontSize: '13px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                        />

                        <button
                          onClick={() => handleDeleteStation(station.id)}
                          style={{
                            padding: '6px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#EF4444',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#FEE2E2';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div style={{ padding: '0 12px 12px 44px' }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#64748B',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          marginBottom: '6px',
                          textAlign: 'left'
                        }}>
                          Comment
                        </div>
                        <textarea
                          value={station.remarks || ''}
                          onChange={(e) => handleStationChange(station.id, 'remarks', e.target.value)}
                          rows={2}
                          placeholder="Add a comment for this station reject…"
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            fontSize: '13px',
                            color: '#1F2937',
                            resize: 'vertical',
                            outline: 'none',
                            boxSizing: 'border-box',
                            fontFamily: 'inherit',
                            background: '#FFFFFF',
                            lineHeight: 1.5
                          }}
                        />
                      </div>

                      {stationErrors[station.id] && (
                        <div style={{
                          padding: '0 12px 8px 44px',
                          color: '#EF4444',
                          fontSize: '12px',
                          textAlign: 'left'
                        }}>
                          {stationErrors[station.id]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Save button sticky at bottom */}
          <div style={{
            position: 'sticky',
            bottom: '24px',
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '24px',
            paddingTop: '16px',
            zIndex: 10
          }}>
            <Button
              label={
                typeof isLastDprSection === 'boolean'
                  ? (isLastDprSection ? 'Save section' : 'Save & Next')
                  : 'Save Quality Report'
              }
              variant="secondary"
              size="large"
              icon={Save}
              onClick={handleSave}
              disabled={isSaving}
            />
          </div>
        </div>
      </div>
      {/* Delete Confirmation Modal (match Untitled-4) */}
      <ConfirmationModal
        title="Delete Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isOpen={isDeleteModalOpen}
        size="medium"
        confirmButtonProps={{
          style: {
            backgroundColor: '#EF4444',
            borderColor: '#EF4444'
          }
        }}
      />
      <ConfirmationModal
        title="Skip Quality"
        message={`Are you sure you want to skip the Quality log for ${shift === 'night' ? 'night' : 'day'} shift?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isOpen={isSkipConfirmOpen}
        onConfirm={handleConfirmSkipQuality}
        onCancel={() => setIsSkipConfirmOpen(false)}
        size="medium"
      />
    </div>
  );
};

export default QualityLog;
